import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { Layout } from './components/Layout';
import { FloatingConsultationPanel } from './components/call/FloatingConsultationPanel';
import { CallSessionProvider } from './lib/call/CallSessionContext';
import { isAuthenticated, restoreSession } from './lib/auth';
import { apiFetch } from './lib/api';

// Route-split chunks
const Login = lazy(() => import('./routes/Login').then(m => ({ default: m.Login })));
const ForgotPassword = lazy(() => import('./routes/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const Dashboard = lazy(() => import('./routes/Dashboard').then(m => ({ default: m.Dashboard })));
const PatientList = lazy(() => import('./routes/patients/PatientList').then(m => ({ default: m.PatientList })));
const PatientDetail = lazy(() => import('./routes/patients/PatientDetail').then(m => ({ default: m.PatientDetail })));
const LabReportAnnotate = lazy(() => import('./routes/patients/LabReportAnnotate').then(m => ({ default: m.LabReportAnnotate })));
const ConsultationList = lazy(() => import('./routes/consultations/ConsultationList').then(m => ({ default: m.ConsultationList })));
const ConsultationDetail = lazy(() => import('./routes/consultations/ConsultationDetail').then(m => ({ default: m.ConsultationDetail })));
const Profile = lazy(() => import('./routes/Profile').then(m => ({ default: m.Profile })));
const Schedule = lazy(() => import('./routes/Schedule').then(m => ({ default: m.Schedule })));
const ContentReview = lazy(() => import('./routes/ContentReview').then(m => ({ default: m.ContentReview })));

// Consultation-room sections. Lazy like every other route chunk: with the flag
// off, `ConsultationDetail` never renders an <Outlet />, so none of this code is
// fetched at all and the v1 bundle is unchanged.
const OverviewSection = lazy(() =>
  import('./features/consultation-room/sections/OverviewSection').then(m => ({ default: m.OverviewSection })));
const DocumentsSection = lazy(() =>
  import('./features/consultation-room/sections/DocumentsSection').then(m => ({ default: m.DocumentsSection })));
const CarePlanSection = lazy(() =>
  import('./features/consultation-room/sections/CarePlanSection').then(m => ({ default: m.CarePlanSection })));
const PlaceholderSection = lazy(() =>
  import('./features/consultation-room/sections/PlaceholderSection').then(m => ({ default: m.PlaceholderSection })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

// A single layout route (rendered once, children swapped via <Outlet />) —
// not one <ProtectedShell> element instantiated per <Route>. That distinction
// matters: with a distinct element per route, navigating between routes
// unmounts and remounts this whole subtree, taking FloatingConsultationPanel
// (and the live call it holds) down with it. As one shared parent route, this
// mounts once and survives every navigation among its child routes.
function ProtectedShell() {
  const location = useLocation();
  // The access token lives only in memory, so a fresh page load has none yet:
  // try the HttpOnly refresh cookie once before deciding to show the login page.
  const [restoring, setRestoring] = useState(() => !isAuthenticated());
  useEffect(() => {
    if (!restoring) return;
    let active = true;
    void restoreSession().finally(() => { if (active) setRestoring(false); });
    return () => { active = false; };
  }, [restoring]);

  if (restoring) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory">
        <p className="font-body text-body text-stone">Loading…</p>
      </div>
    );
  }
  if (!isAuthenticated()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <SignedInShell />;
}

function SignedInShell() {
  // Doctor name for the sidebar.
  const { data } = useQuery({
    queryKey: ['doctor-me'],
    queryFn: () => apiFetch<{ name: string }>('/v1/doctor/me'),
    staleTime: 300_000,
  });

  return (
    <CallSessionProvider>
      <Layout doctorName={data?.name}>
        <Suspense fallback={
          <div className="flex items-center justify-center h-64">
            <p className="font-body text-body text-stone">Loading…</p>
          </div>
        }>
          <Outlet />
        </Suspense>
      </Layout>
      <FloatingConsultationPanel />
    </CallSessionProvider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            <Route element={<ProtectedShell />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />

              <Route path="/patients" element={<PatientList />} />
              <Route path="/patients/:id" element={<PatientDetail />} />
              <Route path="/patients/:id/lab-reports/:reportId" element={<LabReportAnnotate />} />

              <Route path="/consultations/today" element={<ConsultationList filter="today" />} />
              <Route path="/consultations/upcoming" element={<ConsultationList filter="upcoming" />} />
              <Route path="/consultations/history" element={<ConsultationList filter="history" />} />
              {/* Nested, not five siblings. `ConsultationDetail` (and, inside
                  it, the consultation room's shell — the docked call and the
                  prescription rail) stays mounted while the child section
                  swaps; sibling routes would unmount the shell and drop a live
                  call on every section change. The v1 layout renders no
                  <Outlet />, so with the flag off these children are matched but
                  never rendered and nothing about v1 changes. */}
              <Route path="/consultations/:id" element={<ConsultationDetail />}>
                <Route index element={<OverviewSection />} />
                <Route path="history" element={<PlaceholderSection sectionKey="history" />} />
                <Route path="care-plan" element={<CarePlanSection />} />
                <Route path="documents" element={<DocumentsSection />} />
              </Route>

              <Route path="/schedule" element={<Schedule />} />
              <Route path="/content-review" element={<ContentReview />} />
              <Route path="/profile" element={<Profile />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
